import axios from 'axios';
import { apiAddress } from '../../config';

/**
 * Get track metadata and audio URL
 * @param {string} trackId - Track UUID
 * @param {string} accessToken - JWT access token (required by backend)
 * @returns {Promise} Track data with presigned S3 URL
 */
export const getTrack = async (trackId, accessToken) => {
  const response = await axios.get(`${apiAddress}/audio/tracks/${trackId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return response.data;
};

/**
 * Start playback tracking (creates playback_event)
 * @param {string} trackId - Track UUID
 * @param {string} accessToken - JWT access token (required by backend)
 * @returns {Promise} Playback event ID for heartbeats
 */
export const startPlayback = async (trackId, accessToken) => {
  const response = await axios.post(
    `${apiAddress}/audio/tracks/${trackId}/play`,
    { platform: 'web' },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};

/**
 * Send playback heartbeat (every 10 seconds)
 * @param {number} eventId - Playback event ID
 * @param {number} secondsListened - Cumulative seconds listened
 * @returns {Promise}
 */
export const sendHeartbeat = async (eventId, secondsListened) => {
  const response = await axios.post(
    `${apiAddress}/audio/playback/${eventId}/heartbeat`,
    {
      seconds_listened: secondsListened,
    }
  );
  return response.data;
};

/**
 * Get all tracks for a tour
 * @param {string} tourId - Tour UUID
 * @returns {Promise} Array of tracks
 */
export const getTourTracks = async (tourId) => {
  const response = await axios.get(`${apiAddress}/audio/tours/${tourId}/tracks`);
  return response.data;
};

/**
 * Update the lat/lng of an audio track pin (admin only).
 *
 * TODO: Backend handoff required
 * Need: PATCH /audio/tracks/{track_id}/location
 * Request body: { lat: number, lng: number }
 * Response: { trackId, lat, lng, message }
 * Access control: Requires is_superuser (or equivalent admin check on the backend).
 * Pass this requirement to the backend agent before this call goes live.
 *
 * @param {string} trackId - Track UUID
 * @param {number} lat - New latitude
 * @param {number} lng - New longitude
 * @param {string} accessToken - JWT access token
 * @returns {Promise}
 */
export const updateTrackLocation = async (trackId, lat, lng, accessToken) => {
  const response = await axios.patch(
    `${apiAddress}/audio/tracks/${trackId}/location`,
    { lat, lng },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};
